import React, { useState } from "react";
import ConnectionInviteOverlaySheet from "./ConnectionInviteOverlaySheet";
import { Connection, ParticipantSummary, UserSummary } from "../../gql/types";
import noUser from "../../assets/no-user.png";
import styles from "./ConnectionSelectInputRow.module.css";
import ErrorUnit from "components/input/ErrorUnit";

interface ConnectionSelectInputRowProps {
    onChange?: (participants: ParticipantSummary[]) => void;
    initialParticipants?: ParticipantSummary[];
    className?: string;
    style?: React.CSSProperties;
    placeholder?: string;
    prefixText?: string;
    baseZIndex?: number;
    errorStr?: string;
}

const ConnectionSelectInputRow: React.FC<ConnectionSelectInputRowProps> = ({
    onChange,
    initialParticipants = [],
    className,
    style,
    placeholder,
    prefixText = "and",
    baseZIndex,
    errorStr,
}) => {
    const [selectedParticipants, setSelectedParticipants] = useState<ParticipantSummary[]>(initialParticipants);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(initialParticipants.map(p => p.id)));
    const [showOverlay, setShowOverlay] = useState(false);

    const handleOpen = () => setShowOverlay(true);
    const handleClose = () => setShowOverlay(false);

    const handleConfirm = (connections: Connection[]) => {
        const participants = connections.map((connection: Connection) => {
            return connection.participant;
        })
        setSelectedParticipants(participants);
        setSelectedIds(new Set(participants.map(p => p.id)));
        onChange?.(participants);
        handleClose();
    };

    const main = selectedParticipants[0];
    const others = selectedParticipants.slice(1);

    const isUser = (p: ParticipantSummary): p is UserSummary => "email" in p;

    const displayName = main
        ? isUser(main)
            ? `${main.firstName || ""} ${main.lastName || ""}`.trim()
            : main.name
        : (placeholder ?? "");

    const profilePictureUrl = main
        ? isUser(main)
            ? main.profilePictureUrl
            : main.parents?.[0]?.profilePictureUrl
        : null;

    const altText = main
        ? isUser(main)
            ? main.email
            : main.name
        : (placeholder ?? "Select Connections");

    return (
        <>
            <div className={styles.selectableRowContainer}>
                <div
                    className={`${styles.rowContainer} ${errorStr && errorStr !== null ? styles.errorFocus : ''} ${className || ""}`}
                    onClick={handleOpen}
                    style={style}
                >
                    <div className={styles.userSummary}>
                        <img
                            src={profilePictureUrl || noUser}
                            alt={altText}
                            className={styles.avatar}
                        />
                        <span className={`${styles.userName} ${selectedParticipants.length === 0 ? styles.placeholder : ''}`}>{displayName}</span>
                    </div>

                    {others.length > 0 && (
                        <span className={styles.othersLabel}>
                            {prefixText} {others.length} others
                        </span>
                    )}
                </div>
                <ErrorUnit isFocused={false} errorStr={errorStr ?? null} />
            </div>

            {showOverlay && (
                <ConnectionInviteOverlaySheet
                    initialSelectedIds={selectedIds}
                    onBack={handleClose}
                    onConfirm={handleConfirm}
                    baseZIndex={baseZIndex}
                />
            )}
        </>
    );
};

export default ConnectionSelectInputRow;
